<template>
  <div>
    <!-- Gallery Modal Overlay -->
    <Transition name="gallery-fade">
      <div
        v-if="isOpen"
        class="gallery-overlay"
        @click="closeGallery"
        @keydown="handleKeydown"
        tabindex="0"
        ref="galleryOverlay"
      >
        <!-- Close Button -->
        <button
          @click="closeGallery"
          class="gallery-close-btn"
          aria-label="Close gallery"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="w-6 h-6"
          >
            <path
              fill-rule="evenodd"
              d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
              clip-rule="evenodd"
            />
          </svg>
        </button>

        <!-- Main Image Container -->
        <div
          class="gallery-main-container"
          @click.stop
          @touchstart="handleTouchStart"
          @touchend="handleTouchEnd"
        >
          <!-- Navigation Arrows -->
          <button
            v-if="allImages.length > 1"
            @click="previousImage"
            class="gallery-nav-btn gallery-nav-left"
            aria-label="Previous image"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="w-6 h-6"
            >
              <path
                fill-rule="evenodd"
                d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.91 6.91a.75.75 0 11-1.06 1.06l-7.5-7.5z"
                clip-rule="evenodd"
              />
            </svg>
          </button>

          <button
            v-if="allImages.length > 1"
            @click="nextImage"
            class="gallery-nav-btn gallery-nav-right"
            aria-label="Next image"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="w-6 h-6"
            >
              <path
                fill-rule="evenodd"
                d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z"
                clip-rule="evenodd"
              />
            </svg>
          </button>

          <!-- Current Image -->
          <div class="gallery-image-container">
            <img
              :src="currentImage.src"
              :alt="currentImage.alt"
              class="gallery-image"
              @load="onImageLoad"
              @error="onImageError"
            />

            <!-- Loading Spinner -->
            <div v-if="isLoading" class="gallery-loading">
              <div class="gallery-spinner"></div>
            </div>
          </div>

          <!-- Image Counter -->
          <div v-if="allImages.length > 1" class="gallery-counter">
            {{ currentIndex + 1 }} / {{ allImages.length }}
          </div>

          <!-- Image Caption -->
          <div v-if="currentImage.alt" class="gallery-caption">
            {{ currentImage.alt }}
          </div>
        </div>

        <!-- Thumbnail Navigation -->
        <div v-if="allImages.length > 1" class="gallery-thumbnails" @click.stop>
          <button
            v-for="(image, index) in allImages"
            :key="index"
            @click="goToImage(index)"
            class="gallery-thumbnail"
            :class="{ active: index === currentIndex }"
            :aria-label="`Go to image ${index + 1}`"
          >
            <img
              :src="image.thumbnailSrc"
              :alt="image.alt"
              class="thumbnail-image"
            />
          </button>
        </div>
      </div>
    </Transition>

    <!-- Hidden trigger for programmatic opening -->
    <div ref="galleryTrigger" style="display: none"></div>
  </div>
</template>

<script>
export default {
  name: "BlogPostGallery",
  props: {
    images: {
      type: Array,
      required: true,
      // Each image should have: { src, alt, thumbnailSrc }
    },
  },
  data() {
    return {
      isOpen: false,
      currentIndex: 0,
      isLoading: false,
      touchStartX: 0,
      touchStartY: 0,
      touchEndX: 0,
      touchEndY: 0,
    };
  },
  computed: {
    allImages() {
      return this.images || [];
    },
    currentImage() {
      return this.allImages[this.currentIndex] || {};
    },
  },
  methods: {
    openGallery(initialIndex = 0) {
      console.log(
        "BlogPostGallery: Opening gallery with index:",
        initialIndex,
        "Images:",
        this.allImages
      );
      this.currentIndex = Math.max(
        0,
        Math.min(initialIndex, this.allImages.length - 1)
      );
      this.isOpen = true;
      // Focus the overlay for keyboard navigation
      this.$nextTick(() => {
        if (this.$refs.galleryOverlay) {
          this.$refs.galleryOverlay.focus();
        }
      });
      // Prevent body scroll
      document.body.style.overflow = "hidden";
    },
    closeGallery() {
      this.isOpen = false;
      // Restore body scroll
      document.body.style.overflow = "";
    },
    nextImage() {
      if (this.allImages.length > 1) {
        this.currentIndex = (this.currentIndex + 1) % this.allImages.length;
        this.isLoading = true;
      }
    },
    previousImage() {
      if (this.allImages.length > 1) {
        this.currentIndex =
          this.currentIndex === 0
            ? this.allImages.length - 1
            : this.currentIndex - 1;
        this.isLoading = true;
      }
    },
    goToImage(index) {
      if (index >= 0 && index < this.allImages.length) {
        this.currentIndex = index;
        this.isLoading = true;
      }
    },
    handleKeydown(event) {
      switch (event.key) {
        case "Escape":
          this.closeGallery();
          break;
        case "ArrowLeft":
          this.previousImage();
          break;
        case "ArrowRight":
          this.nextImage();
          break;
        case "ArrowUp":
          event.preventDefault();
          this.previousImage();
          break;
        case "ArrowDown":
          event.preventDefault();
          this.nextImage();
          break;
      }
    },
    onImageLoad() {
      this.isLoading = false;
    },
    onImageError() {
      this.isLoading = false;
      console.error("Failed to load image:", this.currentImage.src);
    },
    handleTouchStart(event) {
      this.touchStartX = event.changedTouches[0].screenX;
      this.touchStartY = event.changedTouches[0].screenY;
    },
    handleTouchEnd(event) {
      this.touchEndX = event.changedTouches[0].screenX;
      this.touchEndY = event.changedTouches[0].screenY;
      this.handleSwipe();
    },
    handleSwipe() {
      const swipeThreshold = 50; // Minimum distance for a swipe
      const xDiff = this.touchEndX - this.touchStartX;
      const yDiff = this.touchEndY - this.touchStartY;

      // Only trigger swipe if horizontal movement is significant
      if (
        Math.abs(xDiff) > swipeThreshold &&
        Math.abs(xDiff) > Math.abs(yDiff)
      ) {
        if (xDiff > 0) {
          // Swipe right - go to previous image
          this.previousImage();
        } else {
          // Swipe left - go to next image
          this.nextImage();
        }
      }
    },
  },
  mounted() {
    // Add global keyboard listener for when overlay is focused
    document.addEventListener("keydown", this.handleKeydown);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleKeydown);
    // Restore body scroll if component is destroyed while open
    if (this.isOpen) {
      document.body.style.overflow = "";
    }
  },
};
</script>

<style scoped>
.gallery-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  outline: none;
}

.gallery-close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10000;
}

.gallery-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.gallery-main-container {
  position: relative;
  max-width: 90vw;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 6rem;
  /* Improve touch experience */
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.gallery-image-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  max-height: 100%;
}

.gallery-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 0.5rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  /* Smooth transitions for swipe navigation */
  transition: opacity 0.3s ease;
}

.gallery-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.gallery-spinner {
  width: 3rem;
  height: 3rem;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.gallery-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10000;
}

.gallery-nav-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-50%) scale(1.1);
}

.gallery-nav-left {
  left: -4rem;
}

.gallery-nav-right {
  right: -4rem;
}

.gallery-counter {
  position: absolute;
  top: -3rem;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.5);
  padding: 0.5rem 1rem;
  border-radius: 1rem;
}

.gallery-caption {
  margin-top: 1rem;
  color: white;
  text-align: center;
  max-width: 80%;
  font-size: 0.875rem;
  line-height: 1.4;
}

.gallery-thumbnails {
  position: fixed;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.5rem;
  max-width: 90vw;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 0.5rem;
  backdrop-filter: blur(10px);
}

.gallery-thumbnail {
  flex-shrink: 0;
  width: 4rem;
  height: 4rem;
  border: 2px solid transparent;
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  background: none;
  padding: 0;
}

.gallery-thumbnail:hover {
  border-color: rgba(255, 255, 255, 0.5);
  transform: scale(1.05);
}

.gallery-thumbnail.active {
  border-color: white;
  transform: scale(1.1);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Transitions */
.gallery-fade-enter-active,
.gallery-fade-leave-active {
  transition: opacity 0.3s ease;
}

.gallery-fade-enter-from,
.gallery-fade-leave-to {
  opacity: 0;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .gallery-overlay {
    padding: 0.5rem;
  }

  .gallery-close-btn {
    top: 0.5rem;
    right: 0.5rem;
    width: 2.5rem;
    height: 2.5rem;
  }

  .gallery-nav-btn {
    width: 2.5rem;
    height: 2.5rem;
  }

  .gallery-nav-left {
    left: 0.5rem;
  }

  .gallery-nav-right {
    right: 0.5rem;
  }

  .gallery-counter {
    top: -2.5rem;
    font-size: 0.75rem;
    padding: 0.25rem 0.75rem;
  }

  .gallery-caption {
    font-size: 0.75rem;
    margin-top: 0.5rem;
  }

  .gallery-thumbnails {
    margin-top: 0.5rem;
    gap: 0.25rem;
  }

  .gallery-thumbnail {
    width: 3rem;
    height: 3rem;
  }

  .gallery-main-container {
    margin-bottom: 5rem;
  }

  /* Add swipe hint on mobile */
  .gallery-main-container::before {
    content: "Swipe left/right to navigate";
    position: absolute;
    top: -2rem;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.75rem;
    font-weight: 500;
    background: rgba(0, 0, 0, 0.5);
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
    pointer-events: none;
    opacity: 0.8;
    white-space: nowrap;
    min-width: 200px;
    text-align: center;
  }

  /* Move counter higher on mobile to avoid clash */
  .gallery-counter {
    top: -4rem;
  }
}

@media (max-width: 480px) {
  .gallery-nav-btn {
    width: 2rem;
    height: 2rem;
  }

  .gallery-nav-left {
    left: 0.25rem;
  }

  .gallery-nav-right {
    right: 0.25rem;
  }

  .gallery-thumbnail {
    width: 2.5rem;
    height: 2.5rem;
  }

  .gallery-thumbnails {
    bottom: 0.25rem;
    padding: 0.25rem;
    gap: 0.125rem;
  }

  .gallery-main-container {
    margin-bottom: 4rem;
  }

  /* Move counter even higher on very small screens */
  .gallery-counter {
    top: -4.5rem;
  }

  /* Adjust swipe hint for very small screens */
  .gallery-main-container::before {
    min-width: 180px;
    font-size: 0.7rem;
    padding: 0.2rem 0.6rem;
  }
}
</style>
